/**
 * Agent Configuration Service
 * 
 * Handles CRUD operations for AI agents
 * Backend Response Format: { data, message, status, statusCode }
 * 
 * @module services/agentconfig
 * 
 * NOTE: Legacy agent configuration screens used to call the old agentic
 * orchestrator. Keep this compatibility layer pointed at the current Skills
 * backend so old imports cannot hit deprecated /api/agents endpoints.
 */

import { skillsApiUrl, skillsAuthHeaders } from '@/services/skills';
import { httpError } from "@/lib/http/apiError";

// Agent Configuration Types
export interface AgentType {
  id?: number;
  code?: string;
  name?: string;
  description?: string;
}

export type Capability = any;

export interface AgentConfig {
  max_tokens?: number;
  temperature?: number;
  code_style?: string;
  include_tests?: boolean;
  model?: string;
  [key: string]: any; // Allow additional config properties
}

export interface LLMModel {
  id: number;
  name: string;
  max_output_tokens: number;
  provider: string;
  model_id: string;
  context_window: number;
  display_name: string;
  supports_function_calling: boolean;
  supports_streaming: boolean;
  supports_vision: boolean;
}

export interface ActivePrompt {
  id: number;
  version: number;
  prompt: string;
  inserted_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface Agent {
  id?: string | number;
  uuid?: string;
  name: string;
  user_id?: string;
  type?: string; // Legacy: agent type code (string)
  agent_type?: AgentType; // New: full agent type object
  status?: string;
  config?: AgentConfig;
  description?: string;
  llm_model_id?: number;
  metadata?: Record<string, any>;
  capabilities?: (string | number | Capability)[]; // Can be codes (string), IDs (number), or objects (Capability)
  error_count?: number;
  success_count?: number;
  last_activity_at?: string | null;
  inserted_at?: string;
  updated_at?: string;
  created_at?: string;
  category?: string | null;
  agent_type_id?: number | null;
  llm_model?: LLMModel;
  active_prompt?: ActivePrompt;
}

export interface CapabilityConfig {
  capability_id: number;
  config: Record<string, any>;
}

export interface CreateAgentRequest {
  agent: {
    name: string;
    user_id: string;
    agent_type_id?: number;
    description?: string;
    llm_model_id?: number;
    capabilities?: CapabilityConfig[];
    prompt?: string;
    config?: AgentConfig;
    status?: string;
    metadata?: Record<string, any>;
  };
}

export interface UpdateAgentRequest {
  agent: {
    name?: string;
    user_id?: string;
    status?: string;
    config?: AgentConfig;
    agent_type_id?: number;
    description?: string;
    llm_model_id?: number;
    capabilities?: CapabilityConfig[];
    prompt?: string;
    metadata?: Record<string, any>;
  };
}

export interface AgentResponse {
  agent: Agent;
}

export interface PaginationInfo {
  page_size: number;
  page_number: number;
  total_entries: number;
  total_pages: number;
}

export interface AgentsListResponse {
  agents: Agent[];
  pagination: PaginationInfo;
}

/**
 * Response structure from getAgents API
 */
export interface GetAgentsResponse {
  agents: Agent[];
  pagination: PaginationInfo;
}

/**
 * Get all agents
 * GET /api/agents?user_id=xxx&page=1&page_size=10&type=code_generator&status=active
 * 
 * @param params Query parameters for filtering and pagination
 * @returns Agents list with pagination info
 */
export async function getAgents(params?: {
  user_id?: string;
  limit?: number;
  offset?: number;
  type?: string;
  status?: string;
}): Promise<GetAgentsResponse> {
  const limit = params?.limit || 10;
  const offset = params?.offset || 0;
  const page = Math.floor(offset / limit) + 1;
  const search = params?.type ? `?agent_type=${encodeURIComponent(params.type)}` : "";
  const res = await fetch(skillsApiUrl(`/api/agents${search}`), {
    headers: {
      Accept: "application/json",
      ...skillsAuthHeaders(),
    },
    credentials: "include",
  });
  if (!res.ok) throw await httpError(res, "list_agents");

  const payload = await res.json();
  const record = payload && typeof payload === "object" ? payload as Record<string, any> : {};
  const data = record.data && typeof record.data === "object" ? record.data as Record<string, any> : record;
  const agents = Array.isArray(data.agents) ? data.agents.map(toLegacyAgent) : [];
  const pagedAgents = agents.slice(offset, offset + limit);

  return {
    agents: pagedAgents,
    pagination: {
      page_size: limit,
      page_number: page,
      total_entries: agents.length,
      total_pages: Math.max(1, Math.ceil(agents.length / limit)),
    },
  };
}

/**
 * Get a specific agent by ID
 * GET /api/agents/:id
 * 
 * Response format: { data: { id, name, agent_type: {...}, capabilities: [{...}], ... } }
 */
export async function getAgent(agentId: string): Promise<Agent> {
  const res = await fetch(skillsApiUrl(`/api/agents/${encodeURIComponent(agentId)}`), {
    headers: {
      Accept: "application/json",
      ...skillsAuthHeaders(),
    },
    credentials: "include",
  });
  if (!res.ok) throw await httpError(res, "get_agent");
  const payload = await res.json();
  const record = payload && typeof payload === "object" ? payload as Record<string, any> : {};
  const data = record.data && typeof record.data === "object" ? record.data as Record<string, any> : record;
  return toLegacyAgent(data.agent ?? data);
}

/**
 * Create a new agent
 * POST /api/agents
 */
export async function createAgent(
  agentData: CreateAgentRequest['agent']
): Promise<Agent> {
  void agentData;
  throw new Error('Agent creation moved to /agents/new.');
}

/**
 * Update an existing agent
 * PUT /api/agents/:id
 */
export async function updateAgent(
  agentId: string,
  agentData: UpdateAgentRequest['agent']
): Promise<Agent> {
  void agentId;
  void agentData;
  throw new Error('Agent editing moved to the Studio Agents page.');
}

/**
 * Delete an agent
 * DELETE /api/agents/:id
 */
export async function deleteAgent(agentId: string): Promise<void> {
  void agentId;
  throw new Error('Agent deletion is not available from the legacy configuration page.');
}

function toLegacyAgent(raw: Record<string, any>): Agent {
  const metadata = raw.metadata && typeof raw.metadata === "object" ? raw.metadata : {};
  return {
    id: raw.id ?? raw.uuid ?? raw.agent_id,
    uuid: raw.uuid ?? raw.id,
    name: raw.name ?? raw.display_name ?? "Untitled agent",
    user_id: raw.user_id ?? metadata.user_id,
    type: raw.agent_type ?? raw.type,
    status: raw.status ?? "active",
    config: raw.config ?? {},
    description: raw.description ?? metadata.description ?? raw.goal,
    metadata,
    capabilities: raw.capabilities ?? raw.tools ?? [],
    error_count: raw.error_count ?? 0,
    success_count: raw.success_count ?? 0,
    last_activity_at: raw.last_activity_at ?? null,
    inserted_at: raw.inserted_at ?? raw.created_at,
    updated_at: raw.updated_at,
    created_at: raw.created_at,
    category: raw.category ?? null,
    llm_model: raw.llm_model ?? raw.llm_config,
    active_prompt: raw.active_prompt,
  };
}
